import { Controller } from "stimulus"

// #1977: de kaart met gelijktijdige trainingen. Een klik op een groep (of een
// smalle kolom daarin) opent op een groot scherm een kaart onder of boven de
// groep, in de Turbo frame "groepkaart", met een lijn van groep naar kaart.
// De kaart is geen modaal venster: het rooster blijft zichtbaar, en een klik
// ernaast of Escape sluit hem.
//
// Op de telefoon doet dit niets: de link loopt door naar afspraakvenster#open,
// dat hetzelfde overzicht schermvullend toont. Zonder JavaScript is het een
// gewone pagina.
//
// Een open <dialog> houdt ook het automatisch verversen tegen
// (rooster_slepen_controller), dus de groep verdwijnt niet onder de lijn
// vandaan. Laadt het rooster om een andere reden opnieuw, dan sluit de kaart.
const GROOT_SCHERM = "(min-width: 768px)"
const AFSTAND = 40
const MARGE = 12

export default class extends Controller {
  static targets = ["dialog", "frame", "laden", "lijn"]

  connect() {
    this.bijIndrukken = (event) => this.buiten(event)
    this.bijToets = (event) => { if (event.key === "Escape") this.escape() }
    this.bijMaat = () => this.plaats()
    this.bijGeladen = (event) => this.geladen(event)
    this.bijOntbreken = (event) => this.ontbreekt(event)
    this.bijCache = () => this.sluit()
    this.element.addEventListener("turbo:frame-load", this.bijGeladen)
    this.frameTarget.addEventListener("turbo:frame-missing", this.bijOntbreken)
    document.addEventListener("turbo:before-cache", this.bijCache)
  }

  disconnect() {
    this.element.removeEventListener("turbo:frame-load", this.bijGeladen)
    this.frameTarget.removeEventListener("turbo:frame-missing", this.bijOntbreken)
    document.removeEventListener("turbo:before-cache", this.bijCache)
    this.sluit()
  }

  open(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return
    if (!window.matchMedia(GROOT_SCHERM).matches) return

    event.preventDefault()
    event.stopImmediatePropagation()
    const link = event.currentTarget
    this.sluit()
    this.terug = link
    this.anker = this.groepVan(link)
    this.anker.classList.add("is-actief")
    this.frameTarget.innerHTML = this.hasLadenTarget ? this.ladenTarget.innerHTML : ""
    this.frameTarget.src = link.href
    // Niet show(): die zet de focus op de kaart terwijl die nog onderaan de pagina
    // staat, en dan springt de pagina daarheen
    this.dialogTarget.open = true
    this.dialogTarget.focus({ preventScroll: true })
    window.addEventListener("pointerdown", this.bijIndrukken)
    window.addEventListener("keydown", this.bijToets)
    window.addEventListener("resize", this.bijMaat)
    this.plaats()
  }

  sluit(event) {
    event?.preventDefault()
    window.removeEventListener("pointerdown", this.bijIndrukken)
    window.removeEventListener("keydown", this.bijToets)
    window.removeEventListener("resize", this.bijMaat)
    this.anker?.classList.remove("is-actief")
    this.anker = null
    if (this.hasLijnTarget) this.lijnTarget.setAttribute("hidden", "")
    if (!this.dialogTarget.open) return

    this.dialogTarget.close()
    this.frameTarget.removeAttribute("src")
    this.frameTarget.innerHTML = ""
    if (event && this.terug?.isConnected) this.terug.focus()
  }

  // ---- wat de kaart sluit of verplaatst ----

  // Boven de kaart kan nog het afspraakvenster liggen: dat gaat eerst dicht
  escape() {
    if (!this.modaalOpen()) this.sluit(new Event("sluiten"))
  }

  buiten(event) {
    if (event.target.closest?.("dialog") || this.modaalOpen()) return
    this.sluit()
  }

  modaalOpen() {
    return Array.from(this.element.querySelectorAll("dialog[open]")).some((dialog) => dialog !== this.dialogTarget)
  }

  geladen(event) {
    if (event.target === this.frameTarget) {
      this.plaats()
    } else if (event.target.id === "rooster_inhoud") {
      this.sluit()
    }
  }

  ontbreekt(event) {
    event.preventDefault()
    this.sluit()
    event.detail.visit(event.detail.response)
  }

  // ---- plek en lijn ----

  // Het kader van de groep bij een smalle kolom; anders de link zelf
  groepVan(link) {
    const sleutel = link.dataset.inGroep
    if (!sleutel) return link

    return link.closest(".ro-dag")?.querySelector(`.ro-groep[data-groep="${sleutel}"]`) || link
  }

  // Onder de groep als dat past, anders erboven; altijd binnen het rooster
  plaats() {
    if (!this.anker || !this.dialogTarget.open) return

    const basis = this.element.getBoundingClientRect()
    const groep = this.anker.getBoundingClientRect()
    const kaart = this.dialogTarget.getBoundingClientRect()
    const nodig = kaart.height + AFSTAND + MARGE
    const onder = window.innerHeight - groep.bottom >= nodig || groep.top < nodig
    const links = Math.max(0, Math.min(groep.left - basis.left + (groep.width / 2) - (kaart.width / 2), basis.width - kaart.width))
    const boven = onder ? groep.bottom - basis.top + AFSTAND : groep.top - basis.top - AFSTAND - kaart.height
    this.dialogTarget.style.left = `${links}px`
    this.dialogTarget.style.top = `${boven}px`
    this.trekLijn(basis, groep, links, boven, kaart, onder)
    if (this.dialogTarget.getBoundingClientRect().bottom > window.innerHeight) {
      this.dialogTarget.scrollIntoView({ block: "nearest", behavior: "smooth" })
    }
  }

  trekLijn(basis, groep, links, boven, kaart, onder) {
    if (!this.hasLijnTarget) return

    const x1 = groep.left - basis.left + (groep.width / 2)
    const y1 = (onder ? groep.bottom : groep.top) - basis.top
    const x2 = Math.max(links + 28, Math.min(x1, links + kaart.width - 28))
    const y2 = onder ? boven : boven + kaart.height
    const lijn = this.lijnTarget.querySelector("line")
    const [begin, eind] = this.lijnTarget.querySelectorAll("circle")
    lijn.setAttribute("x1", x1)
    lijn.setAttribute("y1", y1)
    lijn.setAttribute("x2", x2)
    lijn.setAttribute("y2", y2)
    begin.setAttribute("cx", x1)
    begin.setAttribute("cy", y1)
    eind.setAttribute("cx", x2)
    eind.setAttribute("cy", y2)
    this.lijnTarget.removeAttribute("hidden")
  }
}
